
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './StorageCleanup.css';

const ITEMS_PER_PAGE = 20;

const StorageCleanup = ({ apiBase }) => {
    const [activeTab, setActiveTab] = useState('cleanup');

    /* ─── Cleanup State ─── */
    const [stats, setStats] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [cleanupPage, setCleanupPage] = useState(1);

    /* ─── Single Docs State ─── */
    const [singleDocsStats, setSingleDocsStats] = useState(null);
    const [selectedSingleDocs, setSelectedSingleDocs] = useState([]);
    const [singleDocsLoading, setSingleDocsLoading] = useState(false);
    const [singleDocsPage, setSingleDocsPage] = useState(1);

    /* ─── Archival State ─── */
    const [archiveData, setArchiveData] = useState(null);
    const [archiveMonths, setArchiveMonths] = useState(1);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [archiving, setArchiving] = useState(false);
    const [archivePage, setArchivePage] = useState(1);

    /* ─── Search / Filter / Sort ─── */
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortDir, setSortDir] = useState('asc');

    /* ─── Pending Approvals / Jobs ─── */
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const pollingRef = useRef(null);

    /* ─── Drive Re-auth State ─── */
    const [driveAuth, setDriveAuth] = useState(null);
    const [showReauthModal, setShowReauthModal] = useState(false);
    const [reauthStep, setReauthStep] = useState(1);
    const [reauthSession, setReauthSession] = useState(null);
    const [reauthCode, setReauthCode] = useState('');
    const [reauthLoading, setReauthLoading] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    /* ─── Helpers ─── */
    const formatSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (d) => {
        if (!d) return '—';
        try { return new Date(d).toLocaleDateString(); }
        catch { return '—'; }
    };

    const paginate = (arr, page) => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return arr.slice(start, start + ITEMS_PER_PAGE);
    };

    const totalPages = (total) => Math.ceil(total / ITEMS_PER_PAGE);

    /* ═══════════════════════════════════════════════
       CLEANUP TAB LOGIC
       ═══════════════════════════════════════════════ */

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiBase}/admin/storage/stats`, { headers });
            setStats(res.data);
            setSelectedFiles([]);
            setCleanupPage(1);
        } catch (err) {
            toast.error('Failed to fetch storage stats.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDriveAuthStatus();
        if (activeTab === 'approvals') fetchJobs();
        // eslint-disable-next-line
    }, [activeTab]);

    const orphanedPageItems = stats?.orphaned_files ? paginate(stats.orphaned_files, cleanupPage) : [];
    const orphanedTotalPages = stats?.orphaned_files ? totalPages(stats.orphaned_files.length) : 0;

    const handleSelectAllOnPage = (e) => {
        const pageIds = orphanedPageItems.map(f => f.id);
        if (e.target.checked) {
            setSelectedFiles(prev => [...new Set([...prev, ...pageIds])]);
        } else {
            setSelectedFiles(prev => prev.filter(id => !pageIds.includes(id)));
        }
    };

    const handleSelectAllFiles = () => {
        if (stats?.orphaned_files) {
            if (selectedFiles.length === stats.orphaned_files.length) {
                setSelectedFiles([]);
            } else {
                setSelectedFiles(stats.orphaned_files.map(f => f.id));
            }
        }
    };

    const toggleFileSelect = (id) => {
        setSelectedFiles(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleCleanup = async () => {
        if (!window.confirm(`Permanently delete ${selectedFiles.length} files? This cannot be undone.`)) return;
        setLoading(true);
        try {
            const res = await axios.post(`${apiBase}/admin/storage/cleanup`, { file_ids: selectedFiles }, { headers });
            const freedMB = (res.data.freed_space_bytes / (1024 * 1024)).toFixed(2);
            toast.success(`Deleted ${res.data.deleted_count} files (${freedMB} MB). Compacting...`);

            try {
                await axios.post(`${apiBase}/admin/storage/compact`, {}, { headers });
                toast.success('Database compacted successfully!');
            } catch { toast.warning('Compaction failed – space may not be reclaimed immediately.'); }

            fetchStats();
        } catch (err) {
            toast.error('Cleanup failed.');
            console.error(err);
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (selectedFiles.length === 0) return;
        setDownloading(true);
        try {
            const res = await axios.post(
                `${apiBase}/admin/storage/download`,
                { file_ids: selectedFiles },
                { headers, responseType: 'blob', timeout: 300000 }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `orphaned_files_${selectedFiles.length}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${selectedFiles.length} files as ZIP`);
        } catch (err) {
            toast.error('Download failed.');
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };

    /* ═══════════════════════════════════════════════
       SINGLE DOCS TAB LOGIC
       ═══════════════════════════════════════════════ */

    const fetchSingleDocsStats = async () => {
        setSingleDocsLoading(true);
        try {
            const res = await axios.get(`${apiBase}/admin/storage/single-docs/stats`, { headers });
            setSingleDocsStats(res.data);
            setSelectedSingleDocs([]);
            setSingleDocsPage(1);
        } catch (err) {
            toast.error('Failed to fetch single docs stats.');
            console.error(err);
        } finally {
            setSingleDocsLoading(false);
        }
    };

    const singleDocsPageItems = singleDocsStats?.candidates ? paginate(singleDocsStats.candidates, singleDocsPage) : [];
    const singleDocsTotalPages = singleDocsStats?.candidates ? totalPages(singleDocsStats.candidates.length) : 0;

    const handleSelectAllSingleDocsOnPage = (e) => {
        const pageIds = singleDocsPageItems.map(f => f.gridfs_id);
        if (e.target.checked) {
            setSelectedSingleDocs(prev => [...new Set([...prev, ...pageIds])]);
        } else {
            setSelectedSingleDocs(prev => prev.filter(id => !pageIds.includes(id)));
        }
    };

    const handleSelectAllSingleDocs = () => {
        if (singleDocsStats?.candidates) {
            if (selectedSingleDocs.length === singleDocsStats.candidates.length) {
                setSelectedSingleDocs([]);
            } else {
                setSelectedSingleDocs(singleDocsStats.candidates.map(f => f.gridfs_id));
            }
        }
    };

    const toggleSingleDocSelect = (id) => {
        setSelectedSingleDocs(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSingleDocsCleanup = async () => {
        if (!window.confirm(`Permanently delete ${selectedSingleDocs.length} split pages? This cannot be undone.`)) return;
        setSingleDocsLoading(true);
        try {
            const res = await axios.post(`${apiBase}/admin/storage/single-docs/cleanup`, { file_ids: selectedSingleDocs }, { headers });
            const freedMB = (res.data.freed_space_bytes / (1024 * 1024)).toFixed(2);
            toast.success(`Deleted ${res.data.deleted_count} split pages (${freedMB} MB).`);
            fetchSingleDocsStats();
        } catch (err) {
            toast.error('Cleanup failed.');
            console.error(err);
            setSingleDocsLoading(false);
        }
    };

    /* ═══════════════════════════════════════════════
       ARCHIVAL TAB LOGIC
       ═══════════════════════════════════════════════ */

    const fetchArchivePreview = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiBase}/admin/storage/archive-preview?months=${archiveMonths}`, { headers });
            setArchiveData(res.data);
            setSelectedOrders([]);
            setArchivePage(1);
        } catch (err) {
            toast.error('Failed to scan for archivable orders.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredOrders = useCallback(() => {
        if (!archiveData?.archivable_orders) return [];
        let orders = [...archiveData.archivable_orders];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            orders = orders.filter(o =>
                (o.order_number || '').toLowerCase().includes(term) ||
                (o.client_name || '').toLowerCase().includes(term)
            );
        }

        if (statusFilter !== 'all') {
            if (statusFilter === 'partial') orders = orders.filter(o => o.is_partial);
            else if (statusFilter === 'complete') orders = orders.filter(o => !o.is_partial);
            else orders = orders.filter(o => o.status === statusFilter);
        }

        orders.sort((a, b) => {
            let valA = a[sortField], valB = b[sortField];
            if (sortField === 'created_at') { valA = new Date(valA || 0).getTime(); valB = new Date(valB || 0).getTime(); }
            if (typeof valA === 'string') return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            return sortDir === 'asc' ? (valA || 0) - (valB || 0) : (valB || 0) - (valA || 0);
        });

        return orders;
    }, [archiveData, searchTerm, statusFilter, sortField, sortDir]);

    const handleSort = (field) => {
        if (sortField === field) setSortDir(p => p === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const sortIcon = (field) => sortField !== field ? '⇅' : sortDir === 'asc' ? '▲' : '▼';

    const filteredOrders = getFilteredOrders();
    const archivePageItems = paginate(filteredOrders, archivePage);
    const archiveTotalPages = totalPages(filteredOrders.length);

    const handleSelectAllOrders = (e) => {
        const pageIds = archivePageItems.map(o => o.order_id);
        if (e.target.checked) {
            setSelectedOrders(prev => [...new Set([...prev, ...pageIds])]);
        } else {
            setSelectedOrders(prev => prev.filter(id => !pageIds.includes(id)));
        }
    };

    const toggleOrderSelect = (id) => {
        setSelectedOrders(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleArchive = async () => {
        const partialSelected = (archiveData?.archivable_orders || []).filter(o => selectedOrders.includes(o.order_id) && o.is_partial);
        let msg = `Archive ${selectedOrders.length} orders to Google Drive?\n\n• Zip files → Upload to Drive → Wait for approval`;
        if (partialSelected.length > 0) msg += `\n\n⚠️ ${partialSelected.length} order(s) have incomplete uploads.`;
        if (!window.confirm(msg)) return;

        setArchiving(true);
        try {
            const res = await axios.post(`${apiBase}/admin/storage/archive-execute`, { order_ids: selectedOrders }, { headers });
            toast.info(`Archive job started (${res.data.job_id?.slice(0, 8)}…). Check the Approvals tab for progress.`);
            setActiveTab('approvals');
        } catch (err) {
            if (err.response?.status === 409) {
                toast.warning(err.response.data.detail || 'Another archive job is already running. Please wait or cancel it first.');
                setActiveTab('approvals');
            } else {
                toast.error('Failed to start archive job.');
            }
            console.error(err);
        } finally {
            setArchiving(false);
        }
    };

    /* ═══════════════════════════════════════════════
       APPROVALS TAB LOGIC
       ═══════════════════════════════════════════════ */

    const fetchJobs = useCallback(async () => {
        setJobsLoading(true);
        try {
            const res = await axios.get(`${apiBase}/admin/storage/archive-jobs?limit=50`, { headers });
            setJobs(res.data.jobs || []);
        } catch (err) { console.error('Failed to fetch archive jobs:', err); }
        finally { setJobsLoading(false); }
        // eslint-disable-next-line
    }, [apiBase]);

    const fetchDriveAuthStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${apiBase}/admin/storage/drive/auth-status`, { headers });
            setDriveAuth(res.data);
        } catch (err) {
            console.error('Failed to fetch Drive auth status:', err);
        }
        // eslint-disable-next-line
    }, [apiBase]);

    useEffect(() => {
        if (activeTab === 'approvals') {
            const hasActive = jobs.some(j => j.status === 'uploading');
            if (hasActive && !pollingRef.current) {
                pollingRef.current = setInterval(() => { fetchJobs(); fetchDriveAuthStatus(); }, 5000);
            } else if (!hasActive && pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        }
        return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
        // eslint-disable-next-line
    }, [activeTab, jobs, fetchJobs, fetchDriveAuthStatus]);

    const handleJobAction = async (jobId, action) => {
        const verb = action === 'approve' ? 'approve deletion of originals' : 'reject (keep originals)';
        if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
        try {
            const res = await axios.post(`${apiBase}/admin/storage/archive-jobs/${jobId}/action`, { action, reason: action === 'reject' ? 'Admin rejected' : undefined }, { headers });
            toast.success(res.data.message);
            fetchJobs();
        } catch (err) { toast.error(`Failed to ${action} archive job.`); console.error(err); }
    };

    const handleCancelJob = async (jobId) => {
        if (!window.confirm('Cancel this job? It will stop after the current order finishes. Already-uploaded orders are preserved.')) return;
        try {
            const res = await axios.post(`${apiBase}/admin/storage/archive-jobs/${jobId}/cancel`, {}, { headers });
            toast.info(res.data.message);
            fetchJobs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to cancel job.');
            console.error(err);
        }
    };

    const handleRetryJob = async (jobId) => {
        if (!window.confirm('Retry this job? A new job will be created for orders that were not yet archived.')) return;
        try {
            const res = await axios.post(`${apiBase}/admin/storage/archive-jobs/${jobId}/retry`, {}, { headers });
            toast.success(`Retry job started (${res.data.retrying_orders} orders). Skipped ${res.data.skipped_already_archived} already archived.`);
            fetchJobs();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to retry job.';
            if (err.response?.status === 409) toast.warning(detail);
            else toast.error(detail);
            console.error(err);
        }
    };

    const isJobStuck = (job) =>
        job.status === 'uploading' && new Date(job.updated_at) < new Date(Date.now() - 30 * 60 * 1000);

    const handleReauthStart = async () => {
        setReauthLoading(true);
        try {
            const res = await axios.post(`${apiBase}/admin/storage/drive/reauth/start`, {}, { headers });
            setReauthSession(res.data);
            setReauthStep(2);
        } catch (err) {
            toast.error('Failed to start re-authorization flow.');
            console.error(err);
        } finally {
            setReauthLoading(false);
        }
    };

    const handleReauthComplete = async () => {
        if (!reauthCode.trim()) return;
        setReauthLoading(true);
        try {
            await axios.post(`${apiBase}/admin/storage/drive/reauth/complete`, {
                session_token: reauthSession.session_token,
                code: reauthCode.trim()
            }, { headers });
            toast.success('Google Drive re-authorized successfully! Archive jobs can now proceed.');
            setShowReauthModal(false);
            setReauthStep(1);
            setReauthSession(null);
            setReauthCode('');
            fetchDriveAuthStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Re-authorization failed. Check the code and try again.');
            console.error(err);
        } finally {
            setReauthLoading(false);
        }
    };

    const closeReauthModal = () => {
        setShowReauthModal(false);
        setReauthStep(1);
        setReauthSession(null);
        setReauthCode('');
    };

    const statusBadge = (s) => {
        const m = {
            uploading:        '⏳ Uploading',
            pending_approval: '⏱️ Pending',
            approved:         '✅ Approved',
            rejected:         '❌ Rejected',
            failed:           '⚠️ Failed',
            interrupted:      '⚠️ Interrupted',
            cancelled:        '🚫 Cancelled',
        };
        return <span className={`job-status-badge ${s}`}>{m[s] || s}</span>;
    };

    /* ═══ Pagination Component ═══ */
    const Pagination = ({ page, total, setPage }) => {
        if (total <= 1) return null;
        const pages = [];
        const maxVisible = 7;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        let end = Math.min(total, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

        if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < total) { if (end < total - 1) pages.push('...'); pages.push(total); }

        return (
            <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Prev</button>
                {pages.map((p, i) => p === '...'
                    ? <span key={`gap-${i}`} className="page-gap">…</span>
                    : <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                )}
                <button disabled={page >= total} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
        );
    };

    /* ═══════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════ */

    const pendingCount = jobs.filter(j => j.status === 'pending_approval').length;

    return (
        <div className="storage-mgmt">
            {/* ─── Header ─── */}
            <div className="sm-header">
                <div className="sm-header-left">
                    <h2 className="sm-title">Storage Management</h2>
                    <p className="sm-subtitle">Manage orphaned files, archive orders, and free up space</p>
                </div>
                <div className="sm-tabs">
                    <button className={`sm-tab ${activeTab === 'cleanup' ? 'active' : ''}`} onClick={() => setActiveTab('cleanup')}>
                        <span className="tab-icon">🧹</span> Orphaned
                    </button>
                    <button className={`sm-tab ${activeTab === 'single-docs' ? 'active' : ''}`} onClick={() => setActiveTab('single-docs')}>
                        <span className="tab-icon">📄</span> Split Pages
                    </button>
                    <button className={`sm-tab ${activeTab === 'archive' ? 'active' : ''}`} onClick={() => setActiveTab('archive')}>
                        <span className="tab-icon">📦</span> Archive
                    </button>
                    <button className={`sm-tab ${activeTab === 'approvals' ? 'active' : ''}`} onClick={() => setActiveTab('approvals')}>
                        <span className="tab-icon">✅</span> Approvals
                        {pendingCount > 0 && <span className="badge-count">{pendingCount}</span>}
                    </button>
                </div>
            </div>

            {loading && <div className="sm-loading"><div className="sm-loading-bar" /></div>}

            {/* ─── Drive Re-auth Banner ─── */}
            {driveAuth?.needs_reauth && (
                <div className="sm-reauth-banner">
                    <div className="sm-reauth-banner-content">
                        <span>🔴 Google Drive authorization required — archive jobs will fail until re-authorized.</span>
                        {driveAuth.last_error && <span className="sm-reauth-error-hint">{driveAuth.last_error}</span>}
                    </div>
                    <button
                        className="sm-btn sm-btn-reauth"
                        onClick={() => { setShowReauthModal(true); setReauthStep(1); }}
                    >
                        Re-authorize Google Drive
                    </button>
                </div>
            )}

            {/* ═══ CLEANUP TAB ═══ */}
            {activeTab === 'cleanup' && (
                <div className="sm-panel fade-in">
                    {/* Action Bar */}
                    <div className="sm-toolbar">
                        <button className="sm-btn sm-btn-outline" onClick={fetchStats} disabled={loading}>
                            🔄 Scan Storage
                        </button>
                        <div className="sm-toolbar-right">
                            <button
                                className="sm-btn sm-btn-secondary"
                                disabled={selectedFiles.length === 0 || downloading}
                                onClick={handleDownload}
                            >
                                {downloading ? '⏳ Zipping...' : `📥 Download (${selectedFiles.length})`}
                            </button>
                            <button
                                className="sm-btn sm-btn-danger"
                                disabled={selectedFiles.length === 0 || loading}
                                onClick={handleCleanup}
                            >
                                🗑️ Delete ({selectedFiles.length})
                            </button>
                        </div>
                    </div>

                    {stats && (
                        <>
                            {/* Stat Cards */}
                            <div className="sm-stat-row">
                                <div className="sm-stat-card">
                                    <div className="sm-stat-icon">📁</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Total GridFS Files</span>
                                        <span className="sm-stat-value">{stats.total_files.toLocaleString()}</span>
                                        <span className="sm-stat-sub">{formatSize(stats.total_size_bytes)}</span>
                                    </div>
                                </div>
                                <div className={`sm-stat-card ${stats.orphaned_count > 0 ? 'sm-stat-warn' : 'sm-stat-ok'}`}>
                                    <div className="sm-stat-icon">{stats.orphaned_count > 0 ? '⚠️' : '✅'}</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Orphaned Files</span>
                                        <span className="sm-stat-value">{stats.orphaned_count.toLocaleString()}</span>
                                        <span className="sm-stat-sub">{formatSize(stats.orphaned_size_bytes)} reclaimable</span>
                                    </div>
                                </div>
                                {selectedFiles.length > 0 && (
                                    <div className="sm-stat-card sm-stat-selected">
                                        <div className="sm-stat-icon">☑️</div>
                                        <div className="sm-stat-info">
                                            <span className="sm-stat-label">Selected</span>
                                            <span className="sm-stat-value">{selectedFiles.length}</span>
                                            <span className="sm-stat-sub">
                                                {formatSize(stats.orphaned_files?.filter(f => selectedFiles.includes(f.id)).reduce((acc, f) => acc + f.size, 0) || 0)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Select All / Deselect */}
                            {stats.orphaned_files?.length > 0 && (
                                <div className="sm-selection-bar">
                                    <button className="sm-link-btn" onClick={handleSelectAllFiles}>
                                        {selectedFiles.length === stats.orphaned_files.length
                                            ? `Deselect all ${stats.orphaned_files.length} files`
                                            : `Select all ${stats.orphaned_files.length} files`}
                                    </button>
                                    <span className="sm-page-info">
                                        Page {cleanupPage} of {orphanedTotalPages} · {stats.orphaned_files.length} files total
                                    </span>
                                </div>
                            )}

                            {/* Table */}
                            {stats.orphaned_files?.length > 0 ? (
                                <>
                                    <div className="sm-table-container">
                                        <table className="sm-table">
                                            <thead>
                                                <tr>
                                                    <th className="sm-th-check">
                                                        <input
                                                            type="checkbox"
                                                            onChange={handleSelectAllOnPage}
                                                            checked={orphanedPageItems.length > 0 && orphanedPageItems.every(f => selectedFiles.includes(f.id))}
                                                        />
                                                    </th>
                                                    <th>Filename</th>
                                                    <th>Size</th>
                                                    <th>Upload Date</th>
                                                    <th>File ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orphanedPageItems.map(f => (
                                                    <tr key={f.id}
                                                        onClick={() => toggleFileSelect(f.id)}
                                                        className={selectedFiles.includes(f.id) ? 'row-selected' : ''}
                                                    >
                                                        <td><input type="checkbox" checked={selectedFiles.includes(f.id)} readOnly /></td>
                                                        <td className="cell-filename" title={f.filename}>{f.filename}</td>
                                                        <td className="cell-size">{formatSize(f.size)}</td>
                                                        <td className="cell-date">{new Date(f.upload_date).toLocaleString()}</td>
                                                        <td className="cell-id"><code>{f.id.slice(0, 12)}…</code></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination page={cleanupPage} total={orphanedTotalPages} setPage={setCleanupPage} />
                                </>
                            ) : (
                                <div className="sm-empty">
                                    <span className="sm-empty-icon">✅</span>
                                    <p>No orphaned files. Storage is clean!</p>
                                </div>
                            )}
                        </>
                    )}

                    {!stats && !loading && (
                        <div className="sm-empty">
                            <span className="sm-empty-icon">🔍</span>
                            <p>Click <strong>Scan Storage</strong> to check for orphaned files.</p>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ SINGLE DOCS TAB ═══ */}
            {activeTab === 'single-docs' && (
                <div className="sm-panel fade-in">
                    {/* Action Bar */}
                    <div className="sm-toolbar">
                        <button className="sm-btn sm-btn-outline" onClick={fetchSingleDocsStats} disabled={singleDocsLoading}>
                            🔄 Scan Archived Orders
                        </button>
                        <div className="sm-toolbar-right">
                            <button
                                className="sm-btn sm-btn-danger"
                                disabled={selectedSingleDocs.length === 0 || singleDocsLoading}
                                onClick={handleSingleDocsCleanup}
                            >
                                🗑️ Delete Selected ({selectedSingleDocs.length})
                            </button>
                        </div>
                    </div>

                    {singleDocsStats && (
                        <>
                            {/* Stat Cards */}
                            <div className="sm-stat-row">
                                <div className="sm-stat-card">
                                    <div className="sm-stat-icon">📦</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Archived Orders</span>
                                        <span className="sm-stat-value">{singleDocsStats.archived_order_count?.toLocaleString() || 0}</span>
                                        <span className="sm-stat-sub">Scanned safely</span>
                                    </div>
                                </div>
                                <div className={`sm-stat-card ${singleDocsStats.candidates_count > 0 ? 'sm-stat-warn' : 'sm-stat-ok'}`}>
                                    <div className="sm-stat-icon">{singleDocsStats.candidates_count > 0 ? '⚠️' : '✅'}</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Duplicate Split Pages</span>
                                        <span className="sm-stat-value">{singleDocsStats.candidates_count?.toLocaleString() || 0}</span>
                                        <span className="sm-stat-sub">{formatSize(singleDocsStats.total_size_bytes)} reclaimable</span>
                                    </div>
                                </div>
                                {selectedSingleDocs.length > 0 && (
                                    <div className="sm-stat-card sm-stat-selected">
                                        <div className="sm-stat-icon">☑️</div>
                                        <div className="sm-stat-info">
                                            <span className="sm-stat-label">Selected</span>
                                            <span className="sm-stat-value">{selectedSingleDocs.length}</span>
                                            <span className="sm-stat-sub">
                                                {formatSize(singleDocsStats.candidates?.filter(f => selectedSingleDocs.includes(f.gridfs_id)).reduce((acc, f) => acc + f.size, 0) || 0)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Select All / Deselect */}
                            {singleDocsStats.candidates?.length > 0 && (
                                <div className="sm-selection-bar">
                                    <button className="sm-link-btn" onClick={handleSelectAllSingleDocs}>
                                        {selectedSingleDocs.length === singleDocsStats.candidates.length
                                            ? `Deselect all ${singleDocsStats.candidates.length} files`
                                            : `Select all ${singleDocsStats.candidates.length} files`}
                                    </button>
                                    <span className="sm-page-info">
                                        Page {singleDocsPage} of {singleDocsTotalPages} · {singleDocsStats.candidates.length} files total
                                    </span>
                                </div>
                            )}

                            {/* Table */}
                            {singleDocsStats.candidates?.length > 0 ? (
                                <>
                                    <div className="sm-table-container">
                                        <table className="sm-table">
                                            <thead>
                                                <tr>
                                                    <th className="sm-th-check">
                                                        <input
                                                            type="checkbox"
                                                            onChange={handleSelectAllSingleDocsOnPage}
                                                            checked={singleDocsPageItems.length > 0 && singleDocsPageItems.every(f => selectedSingleDocs.includes(f.gridfs_id))}
                                                        />
                                                    </th>
                                                    <th>Order ID</th>
                                                    <th>Page #</th>
                                                    <th>Status</th>
                                                    <th>Size</th>
                                                    <th>GridFS ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {singleDocsPageItems.map(f => (
                                                    <tr key={f.gridfs_id}
                                                        onClick={() => toggleSingleDocSelect(f.gridfs_id)}
                                                        className={selectedSingleDocs.includes(f.gridfs_id) ? 'row-selected' : ''}
                                                    >
                                                        <td><input type="checkbox" checked={selectedSingleDocs.includes(f.gridfs_id)} readOnly /></td>
                                                        <td className="cell-id"><code>{f.order_id}</code></td>
                                                        <td>{f.page_number}</td>
                                                        <td><span className={`sm-badge ${f.status}`}>{f.status}</span></td>
                                                        <td className="cell-size">{formatSize(f.size)}</td>
                                                        <td className="cell-id"><code>{f.gridfs_id.slice(0, 12)}…</code></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination page={singleDocsPage} total={singleDocsTotalPages} setPage={setSingleDocsPage} />
                                </>
                            ) : (
                                <div className="sm-empty">
                                    <span className="sm-empty-icon">✅</span>
                                    <p>No duplicate split pages. Storage is clean!</p>
                                </div>
                            )}
                        </>
                    )}

                    {!singleDocsStats && !singleDocsLoading && (
                        <div className="sm-empty">
                            <span className="sm-empty-icon">🔍</span>
                            <p>Click <strong>Scan Archived Orders</strong> to find split pages from orders that have already been backed up to Drive.</p>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ ARCHIVE TAB ═══ */}
            {activeTab === 'archive' && (
                <div className="sm-panel fade-in">
                    <div className="sm-toolbar">
                        <div className="sm-toolbar-left">
                            <label className="sm-label">Keep files newer than</label>
                            <select className="sm-select" value={archiveMonths} onChange={e => setArchiveMonths(Number(e.target.value))}>
                                {[1, 2, 3, 6, 12].map(m => (
                                    <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
                                ))}
                            </select>
                            <button className="sm-btn sm-btn-outline" onClick={fetchArchivePreview} disabled={loading || archiving}>
                                🔍 Scan Orders
                            </button>
                        </div>
                        <button
                            className="sm-btn sm-btn-primary"
                            disabled={selectedOrders.length === 0 || archiving}
                            onClick={handleArchive}
                        >
                            {archiving ? '⏳ Starting...' : `📦 Archive (${selectedOrders.length})`}
                        </button>
                    </div>

                    {archiveData?.drive_status && (
                        <div className={`sm-drive-banner ${archiveData.drive_status.status}`}>
                            {archiveData.drive_status.status === 'healthy'
                                ? `✅ Drive connected — ${archiveData.drive_status.folder_name}`
                                : `⚠️ Drive: ${archiveData.drive_status.message || 'Not configured'}`}
                        </div>
                    )}

                    {archiveData && (
                        <>
                            <div className="sm-stat-row">
                                <div className="sm-stat-card">
                                    <div className="sm-stat-icon">📋</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Archivable Orders</span>
                                        <span className="sm-stat-value">{archiveData.total_orders}</span>
                                        <span className="sm-stat-sub">older than {archiveData.months}mo</span>
                                    </div>
                                </div>
                                <div className="sm-stat-card">
                                    <div className="sm-stat-icon">📄</div>
                                    <div className="sm-stat-info">
                                        <span className="sm-stat-label">Total Files</span>
                                        <span className="sm-stat-value">{archiveData.total_files}</span>
                                        <span className="sm-stat-sub">{formatSize(archiveData.total_size_bytes)}</span>
                                    </div>
                                </div>
                                {selectedOrders.length > 0 && (
                                    <div className="sm-stat-card sm-stat-selected">
                                        <div className="sm-stat-icon">☑️</div>
                                        <div className="sm-stat-info">
                                            <span className="sm-stat-label">Selected</span>
                                            <span className="sm-stat-value">{selectedOrders.length}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Search & Filter */}
                            <div className="sm-filter-bar">
                                <input
                                    className="sm-search"
                                    type="text"
                                    placeholder="Search order # or client name..."
                                    value={searchTerm}
                                    onChange={e => { setSearchTerm(e.target.value); setArchivePage(1); }}
                                />
                                <select className="sm-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setArchivePage(1); }}>
                                    <option value="all">All</option>
                                    <option value="partial">⚠️ Partial Only</option>
                                    <option value="complete">✅ Complete Only</option>
                                    <option value="completed">Completed</option>
                                    <option value="processing">Processing</option>
                                </select>
                                <span className="sm-filter-count">{filteredOrders.length} of {archiveData.total_orders}</span>
                            </div>

                            {filteredOrders.length > 0 ? (
                                <>
                                    <div className="sm-table-container">
                                        <table className="sm-table">
                                            <thead>
                                                <tr>
                                                    <th className="sm-th-check">
                                                        <input type="checkbox" onChange={handleSelectAllOrders}
                                                            checked={archivePageItems.length > 0 && archivePageItems.every(o => selectedOrders.includes(o.order_id))} />
                                                    </th>
                                                    <th className="sortable" onClick={() => handleSort('order_number')}>Order # {sortIcon('order_number')}</th>
                                                    <th className="sortable" onClick={() => handleSort('client_name')}>Client {sortIcon('client_name')}</th>
                                                    <th>Status</th>
                                                    <th className="sortable" onClick={() => handleSort('created_at')}>Created {sortIcon('created_at')}</th>
                                                    <th className="sortable" onClick={() => handleSort('file_count')}>Files {sortIcon('file_count')}</th>
                                                    <th className="sortable" onClick={() => handleSort('total_size_bytes')}>Size {sortIcon('total_size_bytes')}</th>
                                                    <th>Upload</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {archivePageItems.map(o => (
                                                    <tr key={o.order_id}
                                                        onClick={() => toggleOrderSelect(o.order_id)}
                                                        className={`${selectedOrders.includes(o.order_id) ? 'row-selected' : ''} ${o.is_partial ? 'row-partial' : ''}`}
                                                    >
                                                        <td><input type="checkbox" checked={selectedOrders.includes(o.order_id)} readOnly /></td>
                                                        <td>{o.order_number}</td>
                                                        <td>{o.client_name}</td>
                                                        <td><span className={`sm-badge ${o.status}`}>{o.status}</span></td>
                                                        <td>{formatDate(o.created_at)}</td>
                                                        <td>{o.file_count}</td>
                                                        <td>{formatSize(o.total_size_bytes)} {o.is_large && <span title="Over 500MB">⚠️</span>}</td>
                                                        <td>
                                                            {o.is_partial
                                                                ? <span className="upload-partial" title={`${o.stamps_uploaded}/${o.order_quantity}`}>⚠️ {o.upload_completion}%</span>
                                                                : <span className="upload-ok">✅</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination page={archivePage} total={archiveTotalPages} setPage={setArchivePage} />
                                </>
                            ) : (
                                <div className="sm-empty">
                                    <span className="sm-empty-icon">📭</span>
                                    <p>{searchTerm || statusFilter !== 'all' ? 'No orders match filter.' : `No archivable orders older than ${archiveData.months}mo.`}</p>
                                </div>
                            )}
                        </>
                    )}

                    {!archiveData && !loading && (
                        <div className="sm-empty">
                            <span className="sm-empty-icon">📦</span>
                            <p>Select a time range and click <strong>Scan Orders</strong> to find archivable files.</p>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ APPROVALS TAB ═══ */}
            {activeTab === 'approvals' && (
                <div className="sm-panel fade-in">
                    <div className="sm-toolbar">
                        <button className="sm-btn sm-btn-outline" onClick={fetchJobs} disabled={jobsLoading}>
                            🔄 Refresh
                        </button>
                    </div>

                    {jobs.length > 0 ? (
                        <div className="sm-jobs-grid">
                            {jobs.map(job => (
                                <div key={job.job_id} className={`sm-job-card ${job.status}`}>
                                    <div className="sm-job-header">
                                        <div className="sm-job-title">
                                            <strong>{job.backup_id}</strong>
                                            {statusBadge(job.status)}
                                        </div>
                                        <span className="sm-job-date">{new Date(job.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="sm-job-body">
                                        <div className="sm-job-metrics">
                                            <div><span>Orders</span><strong>{job.order_count}</strong></div>
                                            <div><span>Files</span><strong>{job.total_files}</strong></div>
                                            <div><span>Size</span><strong>{formatSize(job.total_size_bytes)}</strong></div>
                                            <div><span>By</span><strong>{job.initiated_by_name}</strong></div>
                                            {job.total_freed_bytes > 0 && <div><span>Freed</span><strong>{formatSize(job.total_freed_bytes)}</strong></div>}
                                        </div>

                                        {job.drive_folder_url && (
                                            <a href={job.drive_folder_url} target="_blank" rel="noreferrer" className="sm-drive-link">
                                                📂 View on Drive
                                            </a>
                                        )}
                                        {job.error_message && <div className="sm-job-error">⚠️ {job.error_message}</div>}
                                        {job.failed_orders?.length > 0 && (
                                            <details className="sm-job-details"><summary>❌ {job.failed_orders.length} failed</summary>
                                                <ul>{job.failed_orders.map((f, i) => <li key={i}>{f.order_id} — {f.reason}</li>)}</ul>
                                            </details>
                                        )}
                                        {job.orders?.length > 0 && (
                                            <details className="sm-job-details"><summary>📦 {job.orders.length} archived</summary>
                                                <ul>{job.orders.map((o, i) => (
                                                    <li key={i}>#{o.order_number} — {o.files_count || o.files_archived} files — {formatSize(o.original_size_bytes || o.size_bytes)}
                                                        {o.drive_url && <a href={o.drive_url} target="_blank" rel="noreferrer"> 🔗</a>}
                                                    </li>
                                                ))}</ul>
                                            </details>
                                        )}
                                    </div>
                                    {isJobStuck(job) && (
                                        <div className="sm-job-stuck-warning">
                                            ⚠️ This job may be stuck — no progress in over 30 min. Try cancelling it.
                                        </div>
                                    )}
                                    <div className="sm-job-actions">
                                        {job.status === 'pending_approval' && (
                                            <>
                                                <button className="sm-btn sm-btn-success" onClick={() => handleJobAction(job.job_id, 'approve')}>✅ Approve Deletion</button>
                                                <button className="sm-btn sm-btn-danger" onClick={() => handleJobAction(job.job_id, 'reject')}>❌ Reject</button>
                                            </>
                                        )}
                                        {job.status === 'uploading' && (
                                            <button className="sm-btn sm-btn-warning" onClick={() => handleCancelJob(job.job_id)}>🚫 Cancel</button>
                                        )}
                                        {['failed', 'interrupted', 'cancelled'].includes(job.status) && (
                                            <button className="sm-btn sm-btn-outline" onClick={() => handleRetryJob(job.job_id)}>🔄 Retry</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="sm-empty">
                            <span className="sm-empty-icon">📋</span>
                            <p>{jobsLoading ? 'Loading...' : 'No archive jobs yet. Start from the Archive tab.'}</p>
                        </div>
                    )}
                </div>
            )}
        {/* ─── Drive Re-auth Modal ─── */}
        {showReauthModal && (
            <div className="sm-modal-overlay" onClick={closeReauthModal}>
                <div className="sm-modal" onClick={e => e.stopPropagation()}>
                    <div className="sm-modal-header">
                        <h3>Re-authorize Google Drive</h3>
                        <button className="sm-modal-close" onClick={closeReauthModal}>✕</button>
                    </div>
                    <div className="sm-modal-body">
                        {reauthStep === 1 ? (
                            <>
                                <p>
                                    Click below to generate an authorization URL. Open it in your browser,
                                    sign in with the Google account that owns the Drive storage, and copy
                                    the authorization code shown by Google.
                                </p>
                                <button
                                    className="sm-btn sm-btn-primary"
                                    onClick={handleReauthStart}
                                    disabled={reauthLoading}
                                >
                                    {reauthLoading ? '⏳ Generating...' : '🔗 Get Authorization URL'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="sm-reauth-url-box">
                                    <p><strong>Step 1:</strong> Open this URL in your browser and authorize access:</p>
                                    <a href={reauthSession.auth_url} target="_blank" rel="noreferrer" className="sm-reauth-url">
                                        🔗 Open Google Authorization Page
                                    </a>
                                </div>
                                <div className="sm-reauth-code-box">
                                    <p><strong>Step 2:</strong> Paste the code shown after you authorize:</p>
                                    <input
                                        className="sm-search"
                                        type="text"
                                        placeholder="Paste authorization code here..."
                                        value={reauthCode}
                                        onChange={e => setReauthCode(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleReauthComplete()}
                                        autoFocus
                                    />
                                    <div className="sm-reauth-actions">
                                        <button
                                            className="sm-btn sm-btn-outline"
                                            onClick={() => { setReauthStep(1); setReauthSession(null); setReauthCode(''); }}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            className="sm-btn sm-btn-primary"
                                            onClick={handleReauthComplete}
                                            disabled={!reauthCode.trim() || reauthLoading}
                                        >
                                            {reauthLoading ? '⏳ Verifying...' : '✅ Complete Authorization'}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        )}
        </div>
    );
};

export default StorageCleanup;
